<template>
  <app-page-container :custom="1600" :min-height="0">
    <app-page-header design="new" title="Mountain crew" compact />
    <section class="ui-page">
      <app-filter-toolbar>
        <template #create>
          <app-button design="new" :disabled="Boolean(activeImport)" @click.native="openImport">Import crew</app-button>
        </template>
        <template #primary>
          <app-input id="crew-search" v-model="search" design="new" type="search" aria-label="Search" placeholder="Name, External ID or Old #" />
        </template>
        <app-select v-model="position" design="new" aria-label="Position" :options="positions" label="name" placeholder="Position" />
        <app-select v-model="rank" design="new" aria-label="Rank" :options="ranks" label="name" placeholder="Rank" />
        <app-select v-model="selectedTags" design="new" aria-label="Tags" :options="tags" multiple label="name" placeholder="Tags" />
        <app-select v-model="latestImport" design="new" aria-label="Latest import" :options="latestImportOptions" label="name" placeholder="Latest import" />
        <template #actions>
          <app-icon-button label="Imports" tooltip-placement="left" @click="openImports"><i class="pi pi-history" aria-hidden="true" /></app-icon-button>
        </template>
        <template v-if="hasFilters" #clear><app-clear-filters-button design="new" icon-only @clear="clearFilters" /></template>
      </app-filter-toolbar>
      <active-import-notice @change="activeImport = $event" />
      <app-content-skeleton v-if="loading" label="Loading crew" :columns="columns" :min-width="tableMinWidth" />
      <app-error-state v-else-if="error" :message="error" @retry="load" />
      <app-card v-else-if="items.length" design="new" variant="table" header-density="compact">
        <app-data-table design="new" density="dense" :columns="columns" :rows="items" clickable :min-width="tableMinWidth" :sort="sort" @sort="setSort" @row-click="openCrew($event.row)">
          <template #cell-name="{ row }"><app-table-user :name="row.full_name"><router-link class="ui-link" :to="profileLink(row)">{{ row.full_name }}</router-link></app-table-user></template>
          <template #cell-positions="{ row }">
            <div v-if="row.positions.length" class="ui-cell-inline">
              <app-status-badge v-for="item in row.positions.slice(0, 3)" :key="item.code" design="new" compact :label="item.name" />
              <span v-if="row.positions.length > 3" v-tooltip="positionNames(row)" class="ui-tooltip-target" tabindex="0">+{{ row.positions.length - 3 }}</span>
            </div><span v-else>—</span>
          </template>
          <template #cell-rank="{ row }"><app-status-badge v-if="row.rank" design="new" compact :label="row.rank.name" :color="rankColor(row.rank)" /><span v-else>—</span></template>
          <template #cell-tags="{ row }"><div v-if="row.tags.length" class="ui-cell-inline"><app-status-badge v-for="item in row.tags.slice(0, 3)" :key="item.code" design="new" compact :label="item.name" /><span v-if="row.tags.length > 3">+{{ row.tags.length - 3 }}</span></div><span v-else>—</span></template>
          <template #cell-registry="{ row }"><app-status-badge design="new" compact :tone="registryTone(row)" :label="registryLabel(row)" /></template>
          <template #cell="{ row, column }"><app-table-user v-if="['created_by', 'updated_by', 'registered_by_name', 'rank_assigned_by_name'].includes(column.key)" :name="cellText(row, column) === '—' ? '' : cellText(row, column)" :photo="row[column.key] && row[column.key].crop && row[column.key].crop.crop_url || ''" /><app-table-text v-else :text="cellText(row, column)" /></template>
        </app-data-table>
      </app-card>
      <app-no-results v-else design="new" title="No crew found" text="" />
      <app-pagination v-if="!loading && !error && count" design="new" placement="embedded" :page="page" :pages="pages" :count="count" :visible-count="items.length" :page-size="pageSize" :loading="loading" edges @change="setPage" />
    </section>
    <crew-import-modal />
  </app-page-container>
</template>

<script>
import {
  getClimbingMountainCrewGrid,
  getClimbingMountainCrewRanks,
  getClimbingMountainCrewPositions,
  getClimbingMountainCrewTags
} from '@/http'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppTableText from '@/components/AppTableText.vue'
import AppTableUser from '@/components/AppTableUser.vue'
import ActiveImportNotice from '../imports/components/ActiveImportNotice'
import CrewImportModal from '../imports/components/CrewImportModal'
import { CREW_COLUMNS, cellText, orderingParam } from './crewColumns'

const PAGE_SIZE = 20
const DEFAULT_RANK_COLOR = '#000000'
const LATEST_IMPORT_OPTIONS = [
  { code: 'in', name: 'In latest import' },
  { code: 'not_in', name: 'Not in latest import' }
]

export default {
  name: 'ClimbingMountainCrewCrew',
  components: { ActiveImportNotice, CrewImportModal, AppContentSkeleton, AppErrorState, AppTableText, AppTableUser },
  data() {
    return {
      activeImport: null,
      items: [],
      count: 0,
      pages: 1,
      page: 1,
      pageSize: PAGE_SIZE,
      loading: true,
      error: '',
      search: '',
      sort: null,
      position: null,
      rank: null,
      selectedTags: [],
      latestImport: null,
      latestImportOptions: LATEST_IMPORT_OPTIONS,
      positions: [],
      ranks: [],
      tags: [],
      timer: null,
      requestId: 0,
      columns: CREW_COLUMNS,
      tableMinWidth: '1200px'
    }
  },
  computed: {
    hasFilters() { return Boolean(this.search || this.position || this.rank || this.selectedTags.length || this.latestImport) },
    ordering() {
      return orderingParam(this.sort)
    },
    params() {
      return {
        search: this.search.trim() || undefined,
        position: this.position ? this.position.code : undefined,
        rank: this.rank ? this.rank.code : undefined,
        tags: this.selectedTags.length ? this.selectedTags.map(item => item.code).join(',') : undefined,
        latest_import: this.latestImport ? this.latestImport.code : undefined,
        ordering: this.ordering
      }
    }
  },
  watch: {
    search() { this.onFilterChange(300) },
    position() { this.onFilterChange() },
    rank() { this.onFilterChange() },
    selectedTags() { this.onFilterChange() },
    latestImport() { this.onFilterChange() }
  },
  created() {
    this.loadDictionaries()
    this.load()
  },
  beforeDestroy() {
    window.clearTimeout(this.timer)
  },
  methods: {
    cellText,
    loadDictionaries() {
      Promise.all([
        getClimbingMountainCrewPositions(),
        getClimbingMountainCrewRanks(),
        getClimbingMountainCrewTags()
      ]).then(([positions, ranks, tags]) => {
        this.positions = positions.data
        this.ranks = ranks.data
        this.tags = tags.data
      }).catch(() => {})
    },
    listParams(extra = {}) {
      return { ...this.params, page_size: this.pageSize, ...extra }
    },
    onFilterChange(delay = 0) {
      window.clearTimeout(this.timer)
      this.timer = window.setTimeout(() => {
        this.page = 1
        this.load()
      }, delay)
    },
    setSort(sort) {
      this.sort = sort
      this.page = 1
      this.load()
    },
    setPage(page) {
      this.page = page
      this.load()
    },
    load() {
      const requestId = ++this.requestId
      this.loading = true
      this.error = ''
      getClimbingMountainCrewGrid(this.listParams({ page: this.page }))
        .then(response => {
          if (requestId !== this.requestId) return
          this.items = response.data.results
          this.count = response.data.count
          this.pages = response.data.pages
        })
        .catch(() => {
          if (requestId === this.requestId) this.error = 'The crew list could not be loaded.'
        })
        .finally(() => {
          if (requestId === this.requestId) this.loading = false
        })
    },
    clearFilters() {
      this.search = ''
      this.position = null
      this.rank = null
      this.selectedTags = []
      this.latestImport = null
    },
    positionNames(row) {
      return row.positions.map(item => item.name).join(', ')
    },
    profileLink(row) {
      return { name: 'climbing-mountain-crew-profile', params: { id: row.id } }
    },
    openCrew(row) {
      this.$router.push(this.profileLink(row)).catch(() => {})
    },
    openImports() {
      this.$router.push({ name: 'climbing-mountain-crew-imports' }).catch(() => {})
    },
    openImport() {
      this.$modal.show('crew-import')
    },
    rankColor(rank) {
      return rank.color && rank.color.toLowerCase() !== DEFAULT_RANK_COLOR ? rank.color : ''
    },
    registryTone(row) {
      if (row.in_latest_registry === true) return 'green'
      return row.in_latest_registry === false ? 'amber' : 'neutral'
    },
    registryLabel(row) {
      if (row.in_latest_registry === true) return 'In latest import'
      return row.in_latest_registry === false ? 'Not in latest import' : 'Never imported'
    }
  }
}
</script>
